import {
  Box,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactNode } from "react";

import type { TableColumn } from "@/components/GenericTable/types";
import { EhrMobileActionsShell } from "./EhrMobileActionsShell";

interface EhrSummaryRecordCardProps<T extends Record<string, any>> {
  row: T;
  columns: TableColumn[];
  actions?: (row: T) => ReactNode;
  index?: number;
}

function formatCellValue(value: unknown): string {
  if (value == null || value === "") return "—";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "—";
}

/** Card do sumário mobile — campos da tabela + ações montadas de verdade. */
export function EhrSummaryRecordCard<T extends Record<string, any>>({
  row,
  columns,
  actions,
  index = 0,
}: EhrSummaryRecordCardProps<T>) {
  const theme = useTheme();
  const titleColumn = columns[0];
  const title = titleColumn
    ? formatCellValue(row[titleColumn.rowName])
    : "Registro";
  const detailColumns = columns.slice(1);
  const actionContent = actions ? actions(row) : null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderLeftWidth: 4,
        borderLeftColor: "primary.main",
        bgcolor: "background.paper",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack spacing={1} sx={{ px: 1.35, py: 1.25, flex: 1, minWidth: 0 }}>
        <Typography
          variant="subtitle2"
          fontWeight={800}
          sx={{
            fontSize: "0.9375rem",
            lineHeight: 1.35,
            letterSpacing: "-0.01em",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>

        {detailColumns.length > 0 ? (
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(
                theme.palette.primary.main,
                index % 2 === 0 ? 0.04 : 0.06
              ),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Stack spacing={0.65}>
              {detailColumns.map((column) => (
                <Stack
                  key={column.rowName}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  gap={1}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{ flexShrink: 0, fontSize: "0.65rem" }}
                  >
                    {column.column}
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    textAlign="right"
                    sx={{
                      minWidth: 0,
                      fontSize: "0.6875rem",
                      display: "-webkit-box",
                      WebkitLineClamp: column.lineClamp ?? 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {formatCellValue(row[column.rowName])}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>

      {actionContent ? (
        <EhrMobileActionsShell>{actionContent}</EhrMobileActionsShell>
      ) : null}
    </Box>
  );
}
